<template>
  <ul v-if="tasks.length > 0" class="overflow-y-auto overflow-x-hidden max-h-80 divide-y divide-gray-100 pt-2">
    <li v-for="task in tasks" :key="task.userTaskId" class="flex gap-3 py-3 group">
      <div class="pt-0.5">
        <TooltipProvider v-if="!task.canCompleteFromOverview && task.statusOption !== 'Complete'">
          <Tooltip>
            <TooltipTrigger as-child>
              <input
                type="checkbox"
                :checked="false"
                disabled
                class="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-not-allowed opacity-50"
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ task.completionBlockedReason }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <input
          v-else
          type="checkbox"
          :checked="task.statusOption === 'Complete'"
          :disabled="task.statusOption === 'Complete'"
          class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer disabled:cursor-default disabled:opacity-50"
          @click.prevent="task.statusOption !== 'Complete' && displayCompleteModal(task)"
        />
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-2">
          <span
            class="text-sm text-gray-900 leading-snug"
            :class="{ 'line-through text-gray-400': task.statusOption === 'Complete' }"
          >
            {{ task.name }}
          </span>
          <div class="flex items-center gap-1.5 shrink-0">
            <Badge
              v-if="task.taskTypeOption !== 'One Off'"
              :variant="getBadgeVariant(task.taskTypeOption)"
              class="text-[10px] px-1.5 py-0"
            >
              {{ task.taskTypeOption }}
            </Badge>
            <TaskDueDateBadge :task="task.userTask" />
            <button
              class="p-1 -m-1 opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity duration-150"
              @click="displayDeleteModal(task)"
            >
              <Trash2Icon class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div class="mt-1 text-xs text-gray-500">
          {{ task.assigneeName }}
        </div>
      </div>
    </li>
  </ul>
  <div v-if="showNoResults" class="py-6 text-sm text-gray-400 text-center">No tasks</div>
  <Modal
    v-if="deleteTaskCandidate"
    :title="'Delete User Task'"
    :body="`Are you sure you want to delete the '${deleteTaskCandidate.name}' user task?`"
    :action="'Delete'"
    :action-color="'red'"
    @close-modal="hideDeleteModal"
    @handle-action="handleDeleteTask"
  />
  <Modal
    v-if="completeTaskCandidate"
    :title="getCompleteModalTitle(completeTaskCandidate)"
    :body="`Are you sure you want to mark '${completeTaskCandidate.name}' as complete?`"
    :action="'Complete'"
    :action-color="'indigo'"
    @close-modal="hideCompleteModal"
    @handle-action="handleCompleteTask"
  />
</template>

<script setup lang="ts">
import type { OverviewTaskItem } from './useOverviewTaskParser';
import Modal from '@/components/shared/Modal.vue';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTaskList } from './useTaskList';
import { computed } from 'vue';
import { Trash2Icon } from 'lucide-vue-next';
import TaskDueDateBadge from '@/components/user_tasks/TaskDueDateBadge.vue';
import type { TaskTypeOption } from './filterConfiguration';

export type TaskListProps = {
  tasks: OverviewTaskItem[];
  isLoading: boolean;
  contactId: string;
};

const props = defineProps<TaskListProps>();
const showNoResults = computed(() => !props.isLoading && props.tasks.length === 0);

const {
  deleteTaskCandidate,
  completeTaskCandidate,
  deleteUserTask,
  completeUserTask,
  displayDeleteModal,
  hideDeleteModal,
  hideCompleteModal,
  displayCompleteModal,
} = useTaskList(props.contactId);

type TaskTypeBadgeVariant = 'blue' | 'yellow' | 'purple' | 'gray';

const getBadgeVariant = (taskType: TaskTypeOption): TaskTypeBadgeVariant => {
  const variants: Record<TaskTypeOption, TaskTypeBadgeVariant> = {
    Onboarding: 'blue',
    Offboarding: 'yellow',
    Training: 'purple',
    'One Off': 'gray',
    All: 'gray',
  };
  return variants[taskType] || 'gray';
};

const getCompleteModalTitle = (task: OverviewTaskItem) => {
  if (task.assigneeOption !== 'Me') return `⚠️ Complete Task for ${task.assigneeName}`;
  return 'Complete Task';
};

const handleDeleteTask = async () => {
  const isSuccess = await deleteUserTask();
  if (isSuccess) emit('task-deleted');
};

const handleCompleteTask = async () => {
  const isSuccess = await completeUserTask();
  if (isSuccess) emit('task-completed');
};

const emit = defineEmits(['task-deleted', 'task-completed']);
</script>
